import React, { useContext } from 'react';
import { InvalidStateError, isDefined } from '@nfctron/nexus-core';

/**
 * Hook to return context, but throws an Error if the context is not active
 * @author filipditrich <ditrich@nfctron.com>
 * @param context Context to use
 * @param errorMessage Error message to throw if context is not active
 * @returns Context value if active
 * @throws InvalidStateError if context is not active
 */
export const useContextRequired = <T extends unknown>(context: React.Context<T>, errorMessage?: string): NonNullable<T> => {
	const requiredContext = React.useContext(context);
	if (!isDefined(requiredContext))
		throw new InvalidStateError(`[${context.displayName}] ${errorMessage ?? 'Context is not active, check your implementation.'}`);

	return requiredContext;
};

/**
 * Creates safe context tuple of Provider and useSafeContext hook
 * @see https://github.com/mantinedev/mantine/blob/master/packages/%40mantine/core/src/core/utils/create-safe-context/create-safe-context.tsx
 * @param errorMessage Error message to throw if context is not active
 * @returns Tuple of Provider and useSafeContext hook
 */
export function createSafeContext<ContextValue>(errorMessage: string) {
	const Context = React.createContext<ContextValue | null>(null);
	const useSafeContext = () => useContextRequired(Context, errorMessage);
	const Provider = ({ children, value }: { value: ContextValue; children: React.ReactNode }) => (
		<Context.Provider value={value}> {children} </Context.Provider>
	);

	return [Provider, useSafeContext] as const;
}

/**
 * Creates optional context tuple of Provider and useOptionalContext hook
 * @param initialValue Initial value of the context
 * @returns Tuple of Provider and useOptionalContext hook
 */
export function createOptionalContext<ContextValue>(initialValue: ContextValue | null = null) {
	const Context = React.createContext<ContextValue | null>(initialValue);
	const useOptionalContext = () => React.useContext(Context);
	const Provider = ({ children, value }: { value: ContextValue; children: React.ReactNode }) => (
		<Context.Provider value={value}>{children}</Context.Provider>
	);

	return [Provider, useOptionalContext] as const;
}
